import { z } from "zod";

// Schema para coordenadas geográficas
const coordinatesSchema = z.object({
  0: z.number().min(-180).max(180), // Longitud
  1: z.number().min(-90).max(90), // Latitud
});

// Schema para geometría
const geometrySchema = z.object({
  coordinates: coordinatesSchema,
  type: z.literal("point"),
});

// Schema para dirección
const addressSchema = z.object({
  street: z.string().max(500, "La dirección es demasiado larga").optional(),
  city: z.string().max(100, "La ciudad es demasiado larga").optional(),
  state: z.string().max(100, "El estado es demasiado largo").optional(),
  country: z.string().max(100, "El país es demasiado largo").optional(),
  geometry: geometrySchema.optional(),
});

// Schema para notificaciones
const notificationSchema = z.object({
  email: z.boolean().default(true),
  push: z.boolean().default(false),
  sms: z.boolean().default(false),
  ws: z.boolean().default(false),
});

// Schema para redes sociales
const socialNetworkSchema = z.object({
  name: z.enum(["facebook", "twitter", "linkedin", "instagram", "web_site"]),
  url: z.string().max(200, "La URL es demasiado larga"),
  displayed: z.boolean().default(true),
});

// Schema para horario de atención
const timetableSchema = z.object({
  weekday: z.array(z.string()).optional(),
});

const attentionSchema = z.object({
  timetable: z.array(timetableSchema).optional(),
});

const scheduleSchema = z.object({
  attention: attentionSchema.optional(),
});

const userTypeSchema = z.enum(["institution", "physician", "patient", "admin"]);

// Schema para usuario
export const userSchema = z.object({
  active: z.boolean(),
  active_imaging: z.boolean().optional(),
  block_upload: z.boolean(),
  blood_factor: z.string().nullable(),
  blood_type: z.string().nullable(),
  category: z.string().nullable(),
  address: addressSchema.nullable(),
  admin_imaging: z.boolean(),
  admin_lab: z.boolean().optional().default(false),
  api_whatsapp_active: z.boolean().optional().default(false),
  report_whatsapp_active: z.boolean().optional().default(false),
  attendances: z.array(z.string()).nullable(),
  uid: z.string(),
  description: z.string().max(2000, "La descripción es demasiado larga").nullable(),
  created_at: z.any(), // Soporta Timestamps de Firestore y fechas standard
  customized: z.boolean(),
  days_link: z.number().int().min(1).max(365).default(31),
  dental_images_only: z.boolean().nullable(),
  domain: z.string().nullable(),
  edit_report_vm: z.boolean(),
  email: z.email(),
  email_support: z.email().nullable(),
  logo_email: z.string().nullable(),
  name: z.string().max(200, "El nombre es demasiado largo"),
  planId: z.string().nullable().optional(),
  notification: notificationSchema,
  phone: z.array(z.e164()),
  phone_support: z.e164().optional(),
  social_networks: z.array(socialNetworkSchema),
  schedule: scheduleSchema.nullable(),
  template_name: z.string().max(100, "El nombre de la plantilla es demasiado largo"),
  timezone: z.string().max(50, "La zona horaria es demasiado larga"),
  id_tax: z.string().max(50, "El ID fiscal es demasiado largo").nullable(),
  user: z.string(),
  forename: z.string().max(200, "El nombre es demasiado largo").optional(),
  surname: z.string().max(200, "El apellido es demasiado largo").optional(),
  // TODO: Add the correct enum for subType, for example pharmacy, dental, etc.
  subType: z.string().optional(),
  type: userTypeSchema,
  statusRegistration: z.enum(["pending", "approved", "rejected"]).optional(),
  updated_at: z.string().optional(),
  last_login: z.string().optional(),
  prefix_dicom: z.string().optional(),
  permission_admin: z
    .array(
      z.enum([
        "admin",
        "center_admin",
        "admin_it",
        "imaging",
        "laboratory",
        "bionalista",
        "users",
        "administration",
        "support",
        "deploy",
      ])
    )
    .optional()
    .default([]),
  vm_admin: z.boolean().optional().default(false),
  auth: z
    .object({
      last_login: z.string().nullable().optional(),
      active: z.boolean().default(true),
    })
    .optional(),
});

// Schema para crear usuario
export const createUserSchema = userSchema
  .omit({
    uid: true,
    created_at: true,
    updated_at: true,
    last_login: true,
  })
  .and(
    z.object({
      id: z.string(),
    })
  );

// Schema para actualizar usuario
export const updateUserSchema = userSchema.partial().omit({
  uid: true,
  created_at: true,
  updated_at: true,
  last_login: true,
});

// Schema para filtros de usuario
export const userFiltersSchema = z.object({
  search: z.string().optional(),
  type: userTypeSchema.optional(),
  types: z.array(userTypeSchema).optional(), // New field for multiple types
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  sortBy: z.enum(["name", "email", "type", "created_at", "last_login"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  active: z.boolean().optional(),
  active_imaging: z.boolean().optional(),
});

// Schema para perfil de usuario
export const userProfileSchema = z.object({
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .max(200, "El nombre es demasiado largo"),
  email: z.email(),
  avatar: z.string().url("URL de avatar inválida").optional(),
  bio: z.string().max(500, "La biografía es demasiado larga").optional(),
  timezone: z.string().optional(),
  language: z.enum(["es", "en", "pt"]).optional(),
  address: addressSchema.optional(),
  phone: z
    .array(z.string().max(20, "El número de teléfono es demasiado largo"))
    .optional(),
});

// Schema para respuesta paginada de usuarios
export const usersResponseSchema = z.object({
  data: z.array(userSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
  message: z.string().optional(),
  success: z.boolean(),
});

// Tipos inferidos de los schemas
export type User = z.infer<typeof userSchema>;
export type AdminPermission = z.infer<typeof userSchema>["permission_admin"] extends
  | (infer T)[]
  | undefined
  ? T
  : never;
export type CreateUserData = z.infer<typeof createUserSchema>;
export type UpdateUserData = z.infer<typeof updateUserSchema>;
export type UserFilters = z.infer<typeof userFiltersSchema>;
export type UserProfile = z.infer<typeof userProfileSchema>;
export type UsersResponse = z.infer<typeof usersResponseSchema>;
export type Address = z.infer<typeof addressSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type SocialNetwork = z.infer<typeof socialNetworkSchema>;
export type Geometry = z.infer<typeof geometrySchema>;
export type Coordinates = z.infer<typeof coordinatesSchema>;

// ---------------------------------------------------------------------------
// Tipos para los contratos de la nueva API (viewmed-users-services)
// Estos tipos son independientes del schema Zod anterior y reflejan exactamente
// la estructura del backend.
// ---------------------------------------------------------------------------

/** Resultado simplificado de búsqueda (sin includeUserData) */
export interface UserSearchHit {
  id: string;
  uid: string;
  email: string;
  name?: string;
  forename?: string;
  surname?: string;
  type: string;
  auth?: {
    last_login: string | null;
    active: boolean;
  };
}

/** Parámetros del endpoint GET /api/v1/users/search */
export interface UserSearchParams {
  q: string;
  type?: string;
  category?: string;
  /** Si true, retorna el documento completo de Firestore en lugar de UserSearchHit */
  includeUserData?: boolean;
  page?: number;
  hitsPerPage?: number;
  sortByActiveImaging?: boolean;
  sortByCreatedAt?: boolean;
  active_imaging?: boolean;
  /** Parámetro para bypass de caché */
  refresh?: string;
}

/** Respuesta del endpoint GET /api/v1/users/search */
export interface UserSearchResponse {
  data: UserSearchHit[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/** Acciones posibles sobre el estado de un usuario */
export type UserStatusAction = "verify" | "enable" | "disable";

/** Respuesta del PATCH /api/v1/users/:uid/status */
export interface UserStatusResult {
  uid: string;
  disabled: boolean;
  active: boolean;
}

/**
 * Campos editables del perfil — refleja el body del PATCH /api/v1/users/:uid/profile.
 * Los campos uid, email y created_at son inmutables y se omiten.
 */
export interface UserProfileUpdate {
  name?: string;
  forename?: string;
  surname?: string;
  phone?: string[];
  timezone?: string;
  language?: string;
  description?: string;
  category?: string;
  subType?: string;
  subTypeId?: string;
  type?: string;
  /** DocId del plan en Firestore (`plans`). Legacy: también puede ser `Plan.name`. */
  planId?: string | null;
  active?: boolean;
  active_imaging?: boolean;
  admin_imaging?: boolean;
  admin_lab?: boolean;
  api_whatsapp_active?: boolean;
  report_whatsapp_active?: boolean;
  block_upload?: boolean;
  customized?: boolean;
  days_link?: number;
  edit_report_vm?: boolean;
  email_support?: string;
  logo_email?: string;
  phone_support?: string;
  permission_admin?: AdminPermission[];
  vm_admin?: boolean;
  notification?: {
    email: boolean;
    push: boolean;
    sms: boolean;
    ws: boolean;
  };
}

/** Respuesta del PATCH /api/v1/users/:uid/profile */
export interface UserProfileUpdateResult {
  uid: string;
  updatedFields: string[];
}

/** Respuesta del POST /api/v1/users/:uid/password */
export interface UserPasswordResult {
  uid: string;
  /** Contraseña generada. Se retorna una única vez. */
  password: string;
}

// ---------------------------------------------------------------------------
// Tipos para API Keys
// ---------------------------------------------------------------------------

/** API Key */
export interface ApiKey {
  id: string;
  name: string;
  permissions: string[];
  user: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

/** Request para crear API Key */
export interface CreateApiKeyRequest {
  name: string;
  permissions: string[];
  user: string;
}

/** Request para actualizar API Key */
export interface UpdateApiKeyRequest {
  name?: string;
  permissions?: string[];
}

/** Respuesta de regenerar API Key */
export interface RegenerateApiKeyResponse {
  id: string;
  value: string;
  updatedAt: string;
}
